/**
 * Shared application shell with top navigation tabs.
 *
 * Renders the nav bar (tabs, user info, sign-out), the new-data banner,
 * and an <Outlet /> for the active page content.  The Trend tab only
 * appears when the user is viewing /trends/:sku.  The Admin tab only
 * appears for admin-role users.
 *
 * Wraps all children in `<FreshnessProvider>` so both the banner and
 * every page can consume the shared freshness context.
 *
 * Responsive behaviour:
 *   - Desktop: single-row header with title, tabs, and user info.
 *   - Tablet/mobile: title hidden below `md`, nav tabs horizontally
 *     scrollable, user info condensed.
 *
 * Session 5.12: responsive polish.
 */
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { FreshnessProvider } from '../hooks/useDataFreshness';
import NewDataBanner from './NewDataBanner';

/** Static navigation items (always visible). */
const NAV_ITEMS = [
  { to: '/', label: 'Price Comparison', end: true },
  { to: '/dashboard', label: 'Dashboard' },
  { to: '/changelog', label: 'Changelog' },
  { to: '/quality', label: 'Data Quality' },
  { to: '/run-report', label: 'Run Report' },
  { to: '/upload', label: 'Upload' },
];

/**
 * Tailwind class string for an active vs inactive NavLink.
 *
 * @param {{ isActive: boolean }} props
 * @returns {string}
 */
function navLinkClass({ isActive }) {
  const base =
    'px-3 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap';
  return isActive
    ? `${base} bg-blue-100 text-blue-700`
    : `${base} text-gray-600 hover:text-gray-900 hover:bg-gray-100`;
}

export default function Layout() {
  const { user, isAdmin, logout } = useAuth();
  const location = useLocation();
  const isTrendPage = location.pathname.startsWith('/trends/');

  return (
    <FreshnessProvider>
    {/* h-dvh (with h-screen fallback) tracks the *visible* viewport on
        mobile — 100vh overshoots under a collapsed browser URL bar,
        hiding the bottom of full-height pages like the comparison
        table. The shell is the single scroll boundary; pages that
        scroll do so in their own containers. */}
    <div className="h-screen supports-[height:100dvh]:h-[100dvh] flex flex-col">
      {/* ----------------------------------------------------------------- */}
      {/* Top navigation bar                                                 */}
      {/* ----------------------------------------------------------------- */}
      <header className="bg-white border-b border-gray-200 shadow-sm flex-shrink-0">
        <div className="mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-14 gap-4">
            {/* App title — hidden on small screens to save space */}
            <div className="flex-shrink-0 hidden md:block">
              <h1 className="text-lg font-semibold text-gray-900">
                Price Monitoring
              </h1>
            </div>

            {/* Tab navigation — horizontally scrollable on small
                screens. The scrollbar is hidden, so a right-edge fade
                on the (non-scrolling) wrapper signals that more tabs
                exist off-screen. */}
            <div
              className="relative min-w-0 after:pointer-events-none
                         after:absolute after:right-0 after:top-0
                         after:h-full after:w-6 after:bg-gradient-to-l
                         after:from-white md:after:hidden"
            >
            <nav
              className="flex space-x-1 overflow-x-auto scrollbar-hide
                         -mx-4 px-4 sm:-mx-0 sm:px-0"
            >
              {NAV_ITEMS.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={navLinkClass}
                >
                  {item.label}
                </NavLink>
              ))}

              {/* Trend tab — only visible when viewing a product trend */}
              {isTrendPage && (
                <span
                  className="px-3 py-2 text-sm font-medium rounded-md
                             bg-blue-100 text-blue-700 whitespace-nowrap"
                >
                  Trend
                </span>
              )}

              {/* Admin tab — admin role only */}
              {isAdmin && (
                <NavLink to="/admin" className={navLinkClass}>
                  Admin
                </NavLink>
              )}
            </nav>
            </div>

            {/* Right side: currency note + user info + sign-out */}
            <div className="flex items-center space-x-3 flex-shrink-0">
              <span className="text-xs text-gray-400 hidden lg:inline">
                All prices in GBP (&pound;)
              </span>
              <span className="text-sm text-gray-600 hidden sm:inline">
                {user?.username}
              </span>
              <button
                type="button"
                onClick={logout}
                className="text-sm text-gray-500 hover:text-gray-700
                           transition-colors whitespace-nowrap"
              >
                Sign out
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* ----------------------------------------------------------------- */}
      {/* New data banner (visible when freshness polling detects new data)   */}
      {/* ----------------------------------------------------------------- */}
      <NewDataBanner />

      {/* ----------------------------------------------------------------- */}
      {/* Page content                                                       */}
      {/* ----------------------------------------------------------------- */}
      {/* The shell is fixed-height, so long pages scroll here instead
          of on the body; full-height pages (Comparison) opt out with
          flex-1 min-h-0 and scroll internally. */}
      <main className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-6 min-w-0 flex flex-col">
        <Outlet />
      </main>
    </div>
    </FreshnessProvider>
  );
}
